import { FC, useState, useEffect } from 'react';
import {
  AlertMessage,
  FAIcon,
  IconStyle,
  InputType,
  InputValidations,
  LoaderType,
  NextgenImage,
  SmartInput,
  Theme,
  useTranslation,
} from '@nextgen-web-framework/all';
import type { AppState } from '@nextgen-web-framework/core';
import { useSelector } from 'react-redux';
import { PhoneNumberDisplay } from '@commerce-profile-webcomponents/ui';
import { ICheckoutSelectedAddressCardProps } from '../types/propTypes';
import { ShippingTypeEnums } from '../../common/config/checkoutConstants';
import CheckoutInlineText from '../../checkout-inline-text/CheckoutInlineText';
import { checkoutFeatureFlagConfig, isValidEmail } from '../../common/transformer';
import styles from './CheckoutSelectedAddressCard.module.scss';

export const CheckoutSelectedAddressCard: FC<ICheckoutSelectedAddressCardProps> = ({
  selectedAddress,
  softFilters,
  shippingType,
  selectedStoreOptionName = '',
  onEditIconClick,
  profileName,
  hideEdit,
  children,
  isEmailCard,
  onChangeEmailCallback,
  emailValue,
  onEmailValidation,
}: ICheckoutSelectedAddressCardProps) => {
  const t = useTranslation();
  const emailRegex: RegExp = useSelector((state: AppState) => state?.commerceCheckout?.emailRegex);
  const [email, setEmail] = useState(emailValue);
  const [emailValidationStatus, setEmailValidationStatus] = useState(InputValidations.DEFAULT);
  const editIcon = (toBeAbsolute?: boolean) => (
    <span
      className={
        toBeAbsolute
          ? `${styles['edit-icon-container']} ${styles['edit-icon-container-absolute']}`
          : styles['edit-icon-container']
      }
      onClick={() => onEditIconClick?.()}
    >
      <FAIcon
        theme={Theme.BLACK}
        data-testid="test-icon-id"
        iconName={'pen-to-square'}
        className={styles['edit-icon']}
      />
      {checkoutFeatureFlagConfig()?.addressEditTextVisibilityFlag && (
        <span className={styles['edit-label']}>{t('checkout.CheckoutSelectedAddressCard.EditLabel')}</span>
      )}
    </span>
  );

  const getPhoneNumberField = (phoneNo: string) => {
    if (shippingType == ShippingTypeEnums.DELIVERY) {
      return (
        <>
          (
          <PhoneNumberDisplay phoneNumber={phoneNo} />)
        </>
      );
    }
    return <PhoneNumberDisplay phoneNumber={phoneNo} />;
  };

  useEffect(() => {
    if (isEmailCard) {
      const status = !email
        ? InputValidations.DEFAULT
        : isValidEmail(email, emailRegex)
          ? InputValidations.VALID
          : InputValidations.INVALID;
      setEmailValidationStatus(status);
      onEmailValidation?.(status == InputValidations.VALID);
    }
  }, [email]);

  return (
    <div className={styles['checkout-shipping-selected-address-container']}>
      {selectedAddress?.heading && (
        <h4
          className={`${styles['checkout-shipping-selected-address-title']} ${
            profileName && styles['title-with-profile']
          } `}
        >
          {t(selectedAddress.heading)}
        </h4>
      )}

      {profileName && (
        <CheckoutInlineText
          text={t('checkout.CheckoutShippingDetailsContainer.profileAddressText', { profileName })}
          customClass={styles['checkout-shipping-selected-profile-name']}
        />
      )}

      {((shippingType == ShippingTypeEnums.DELIVERY && softFilters?.delivery?.isVisible) ||
        (shippingType == ShippingTypeEnums.PICKUP && softFilters?.pickup?.isVisible)) && (
        <AlertMessage
          icon="exclamation-triangle"
          theme={Theme.WARNING}
          iconStyle={IconStyle.REGULAR}
          className={styles['checkout-selected-address-alert-msg']}
        >
          {shippingType == ShippingTypeEnums.DELIVERY
            ? t(softFilters?.delivery.message)
            : t(softFilters?.pickup.message, { selectedStoreOptionName })}
        </AlertMessage>
      )}
      {!isEmailCard && (
        <div className={styles['checkout-shipping-selected-address-card']}>
          <h4 className={styles['checkout-shipping-selected-address-card-label']}>
            {(selectedAddress?.title && selectedAddress?.storeId) || selectedAddress?.imageUrl ? (
              <span>
                {selectedAddress?.imageUrl && (
                  <div className={styles['store-image']}>
                    <NextgenImage
                      src={selectedAddress.imageUrl}
                      alt={selectedAddress?.title || ''}
                      loaderType={LoaderType.CONTENTSTACK}
                    />
                  </div>
                )}
                <span className={styles['checkout-shipping-selected-address-card-store-name']}>
                  {selectedAddress?.title}
                </span>
                {/* {selectedAddress?.storeId && (
                <span className={styles['checkout-shipping-selected-address-card-separator']}></span>
              )}
              <span>{selectedAddress?.storeId}</span> */}
              </span>
            ) : (
              selectedAddress?.title
            )}
            {!hideEdit && editIcon(!selectedAddress?.title)}
          </h4>
          {(selectedAddress?.recipientName || selectedAddress?.recipientContact) && (
            <p className={styles['checkout-shipping-selected-address-card-addressLine']}>
              {selectedAddress?.recipientName ?? ''}
              {selectedAddress?.recipientContact ? <>{getPhoneNumberField(selectedAddress.recipientContact)}</> : ''}
            </p>
          )}
          {selectedAddress?.addressValues?.map((addressLine: string) => (
            <>
              {addressLine && (
                <CheckoutInlineText
                  text={addressLine}
                  customClass={styles['checkout-shipping-selected-address-card-addressLine']}
                />
              )}
            </>
          ))}
          {selectedAddress?.instructions && (
            <CheckoutInlineText
              text={selectedAddress.instructions}
              customClass={styles['checkout-shipping-selected-address-card-instructions']}
            />
          )}
          {children && <div className={styles['checkout-shipping-selected-address-card-children']}>{children}</div>}
        </div>
      )}
      {isEmailCard && (
        <div className={styles['email-field']}>
          <SmartInput
            name="smartInput-element"
            smartInputType={InputType.EMAIL}
            inputValue={emailValue}
            onChange={(e) => {
              setEmail(e.target.value);
              onChangeEmailCallback?.(e.target.value);
            }}
            status={emailValidationStatus}
            emailLabel={t('checkout.CheckoutTaxAndContact.taxInvoiceEmailText')}
          />
        </div>
      )}
    </div>
  );
};

export default CheckoutSelectedAddressCard;
